/**
 * DailyView
 *
 * Daily schedule view with date header, navigation arrows, and item cards.
 * Shared between CalendarView (homepage) and CareScreen.
 *
 * CareScreen injects dose-logging UI via the `renderItemAction` render prop.
 */

import { View, Pressable } from "react-native";
import { Text } from "@louise/ui/Text/Text";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Pill,
  MapPin,
  Loader2,
  Syringe,
  Droplets,
  CircleDot,
  Wind,
  MoreVertical,
  Droplet,
  Sprout,
} from "lucide-react-native";
import { formatDailyDate } from "@src/packages/utils/date";
import { useLocale } from "@src/packages/i18n/useLocale";
import { useTranslations } from "@src/packages/i18n/useTranslations";
import {
  getIntakeTypeIcon,
  type CalendarItem,
  type MedicationIconType,
} from "../calendar.types";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

// Inline MedicationIcon — avoids circular deps and keeps DailyView self-contained
function MedicationIcon({
  intakeType,
  size,
  color,
}: {
  intakeType: MedicationIconType;
  size: number;
  color: string;
}) {
  switch (intakeType) {
    case "injection":
      return <Syringe size={size} color={color} />;
    case "drops":
      return <Droplets size={size} color={color} />;
    case "patch":
      return <CircleDot size={size} color={color} />;
    case "inhaler":
      return <Wind size={size} color={color} />;
    case "cream":
      return <CircleDot size={size} color={color} />;
    case "pill":
    default:
      return <Pill size={size} color={color} />;
  }
}

/**
 * CycleRow — read-only row for period / fertile-window / ovulation days.
 * Reuses the exact card shell from the medication/appointment rows below.
 * Logged days render solid; predicted days are dimmed and outline-only, and
 * ovulation (peak) fills its Sprout — matching the monthly-grid encoding.
 */
function CycleRow({ item }: { item: CalendarItem }) {
  const t = useTranslations();
  const isPredicted = item.meta?.predicted === true;
  const isOvulation =
    item.type === "fertile" && item.meta?.isOvulationDay === true;
  const color = item.color;

  const detail =
    item.type === "period"
      ? (item.subtitle ?? t("care.overview.calendar.prediction.logged"))
      : isOvulation
        ? t("care.overview.calendar.prediction.peak_fertility")
        : t("care.overview.calendar.prediction.fertile_window");

  const badge = isPredicted
    ? t("care.overview.calendar.prediction.predicted")
    : item.type === "period"
      ? t("care.overview.calendar.markers.period")
      : isOvulation
        ? t("care.overview.calendar.prediction.peak")
        : t("care.overview.calendar.prediction.fertile");

  return (
    <View
      style={{
        borderRadius: 16,
        borderWidth: 1,
        borderColor: `${color}30`,
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        opacity: isPredicted ? 0.7 : 1,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          padding: 14,
        }}
      >
        {/* Icon */}
        <View
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: `${color}20`,
          }}
        >
          {item.type === "period" ? (
            <Droplet
              size={18}
              color={color}
              fill={isPredicted ? "transparent" : color}
            />
          ) : (
            <Sprout
              size={18}
              color={color}
              fill={isOvulation && !isPredicted ? color : "transparent"}
            />
          )}
        </View>

        {/* Details */}
        <View style={{ flex: 1 }}>
          <Text
            style={{ fontSize: 14, fontWeight: "500", color: "#ffffff" }}
            numberOfLines={1}
          >
            {item.title}
          </Text>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
              marginTop: 3,
            }}
          >
            <Text style={{ fontSize: 12, fontWeight: "600", color }}>
              {detail}
            </Text>
          </View>
        </View>

        {/* Badge */}
        <View
          style={{
            borderRadius: 999,
            paddingHorizontal: 8,
            paddingVertical: 3,
            backgroundColor: `${color}15`,
          }}
        >
          <Text style={{ fontSize: 10, fontWeight: "600", color }}>
            {badge}
          </Text>
        </View>
      </View>
    </View>
  );
}

export interface DailyViewProps {
  selectedDay: Date;
  items: CalendarItem[];
  isLoading: boolean;
  todayString: string;
  onPreviousDay: () => void;
  onNextDay: () => void;
  /** CareScreen: show "Cycle Day X" under the date header */
  cycleDay?: number | null;
  /**
   * Render prop for injecting action UI below each item card.
   * CareScreen uses this for "Did you take it?" dose-logging buttons.
   * Return null to skip rendering an action for a given item.
   */
  renderItemAction?: (item: CalendarItem) => React.ReactNode;
  /** Called when user taps the ⋮ manage button on a medication */
  onMedicationManagePress?: (item: CalendarItem) => void;
  /** Called when user taps the ⋮ manage button on an appointment */
  onAppointmentManagePress?: (item: CalendarItem) => void;
}

export function DailyView({
  selectedDay,
  items,
  isLoading,
  todayString,
  onPreviousDay,
  onNextDay,
  cycleDay,
  renderItemAction,
  onMedicationManagePress,
  onAppointmentManagePress,
}: DailyViewProps) {
  const { locale } = useLocale();
  const t = useTranslations();
  const capturePress = usePressCapture("DailyView");
  const selectedDayString = `${selectedDay.getFullYear()}-${String(selectedDay.getMonth() + 1).padStart(2, "0")}-${String(selectedDay.getDate()).padStart(2, "0")}`;
  const isSelectedToday = selectedDayString === todayString;

  // Filter to scheduled items only (medications + appointments)
  const scheduledItems = items.filter(
    (item) => item.type === "medication" || item.type === "appointment",
  );

  // Period / fertile-window / ovulation days, surfaced in their own section.
  const cycleItems = items.filter(
    (item) => item.type === "period" || item.type === "fertile",
  );
  const hasFertileEstimate = cycleItems.some((item) => item.type === "fertile");
  const predictionContext = cycleItems.find(
    (item) => item.meta?.predictionConfidence,
  )?.meta;
  const predictionWarnings = [
    ...new Set(
      cycleItems.flatMap((item) => item.meta?.predictionWarnings ?? []),
    ),
  ];

  // Helper: check if a medication is stopped
  const isStopped = (item: CalendarItem): boolean =>
    item.type === "medication" &&
    !!item.meta?.endDate &&
    item.meta.endDate <= todayString;

  return (
    <View>
      {/* Daily Header with Navigation */}
      <View className="mb-4 flex-row items-center justify-between">
        <View>
          <Text
            style={{
              fontSize: 22,
              fontWeight: "500",
              color: "#ffffff",
              fontFamily: "Tobias-Thin",
            }}
          >
            {formatDailyDate(selectedDay, locale)}
          </Text>
          {isSelectedToday && (
            <Text
              style={{
                fontSize: 11,
                fontWeight: "600",
                color: "#4ADE80",
                textTransform: "uppercase",
                letterSpacing: 0.5,
                marginTop: 2,
              }}
            >
              Today
            </Text>
          )}
          {cycleDay != null && (
            <View className="mt-0.5 flex-row items-center gap-1.5">
              <View className="h-1.5 w-1.5 rounded-full bg-[#4ADE80]" />
              <Text className="text-xs font-medium text-[#4ADE80]">
                Cycle Day {cycleDay}
              </Text>
            </View>
          )}
        </View>
        <View style={{ flexDirection: "row", gap: 4 }}>
          <Pressable
            onPress={() => {
              capturePress("previous_day");
              onPreviousDay();
            }}
            style={{
              width: 32,
              height: 32,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 16,
            }}
          >
            <ChevronLeft size={18} color="#94A3B8" />
          </Pressable>
          <Pressable
            onPress={() => {
              capturePress("next_day");
              onNextDay();
            }}
            style={{
              width: 32,
              height: 32,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 16,
            }}
          >
            <ChevronRight size={18} color="#94A3B8" />
          </Pressable>
        </View>
      </View>

      {/* Day Schedule */}
      <Text
        style={{
          fontSize: 10.4,
          fontWeight: "700",
          color: "#64748B",
          textTransform: "uppercase",
          letterSpacing: 1,
          marginBottom: 12,
        }}
      >
        Day Schedule
      </Text>

      {isLoading ? (
        <View
          style={{
            alignItems: "center",
            justifyContent: "center",
            paddingVertical: 32,
          }}
        >
          <Loader2 size={24} color="#64748B" />
        </View>
      ) : scheduledItems.length > 0 ? (
        <View style={{ gap: 10 }}>
          {scheduledItems.map((item) => {
            const time = item.startAt.split("T")[1]?.slice(0, 5) || "";
            const [hours, minutes] = time.split(":").map(Number);
            const ampm = (hours ?? 0) >= 12 ? "PM" : "AM";
            const hours12 = (hours ?? 0) % 12 || 12;
            const formattedTime = `${hours12}:${String(minutes ?? 0).padStart(2, "0")} ${ampm}`;

            const action = renderItemAction?.(item) ?? null;

            const managePress =
              item.type === "medication"
                ? onMedicationManagePress
                : item.type === "appointment"
                  ? onAppointmentManagePress
                  : undefined;

            const stopped = isStopped(item);
            const cardColor = stopped ? "#64748B" : item.color;

            return (
              <View
                key={item.id}
                style={{
                  borderRadius: 16,
                  borderWidth: 1,
                  borderColor: stopped
                    ? "rgba(100, 116, 139, 0.2)"
                    : `${item.color}30`,
                  backgroundColor: stopped
                    ? "rgba(100, 116, 139, 0.05)"
                    : "rgba(255, 255, 255, 0.03)",
                  opacity: stopped ? 0.7 : 1,
                }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 12,
                    padding: 14,
                  }}
                >
                  {/* Icon */}
                  <View
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: `${cardColor}20`,
                    }}
                  >
                    {item.type === "medication" ? (
                      <MedicationIcon
                        intakeType={getIntakeTypeIcon(item.meta?.intakeType)}
                        size={18}
                        color={cardColor}
                      />
                    ) : (
                      <Calendar size={18} color={cardColor} />
                    )}
                  </View>

                  {/* Details */}
                  <View style={{ flex: 1 }}>
                    {/* Title */}
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: "500",
                        color: stopped ? "#94A3B8" : "#ffffff",
                      }}
                      numberOfLines={1}
                    >
                      {item.title}
                    </Text>

                    {/* Info row: time + dose/location + status */}
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 6,
                        marginTop: 3,
                      }}
                    >
                      {/* Time */}
                      <Text
                        style={{
                          fontSize: 12,
                          fontWeight: "600",
                          color: cardColor,
                        }}
                      >
                        {formattedTime}
                      </Text>

                      {/* Dot separator */}
                      {(item.subtitle || item.meta?.location) && (
                        <Text style={{ fontSize: 10, color: "#475569" }}>
                          ·
                        </Text>
                      )}

                      {/* Dose or location */}
                      {item.subtitle && (
                        <Text
                          style={{
                            fontSize: 11,
                            color: stopped ? "#64748B" : "#94A3B8",
                          }}
                          numberOfLines={1}
                        >
                          {item.subtitle}
                        </Text>
                      )}
                      {!item.subtitle && item.meta?.location && (
                        <View
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 3,
                          }}
                        >
                          <MapPin size={9} color="#64748B" />
                          <Text
                            style={{
                              fontSize: 11,
                              color: stopped ? "#64748B" : "#94A3B8",
                            }}
                            numberOfLines={1}
                          >
                            {item.meta.location}
                          </Text>
                        </View>
                      )}
                    </View>
                  </View>

                  {/* Right side: status badge + manage button */}
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {stopped ? (
                      <View
                        style={{
                          borderRadius: 999,
                          paddingHorizontal: 8,
                          paddingVertical: 3,
                          backgroundColor: "rgba(100, 116, 139, 0.15)",
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 10,
                            fontWeight: "600",
                            color: "#94A3B8",
                          }}
                        >
                          Stopped
                        </Text>
                      </View>
                    ) : (
                      item.status && (
                        <View
                          style={{
                            borderRadius: 999,
                            paddingHorizontal: 8,
                            paddingVertical: 3,
                            backgroundColor: `${item.color}15`,
                          }}
                        >
                          <Text
                            style={{
                              fontSize: 10,
                              fontWeight: "600",
                              color: item.color,
                              textTransform: "capitalize",
                            }}
                          >
                            {item.status}
                          </Text>
                        </View>
                      )
                    )}
                    {managePress && (
                      <Pressable
                        onPress={() => {
                          capturePress(
                            pressLabel(`Manage ${item.title}`),
                            item.type === "medication"
                              ? "medication_manage"
                              : "appointment_manage",
                          );
                          managePress(item);
                        }}
                        style={{ padding: 4 }}
                        accessibilityRole="button"
                        accessibilityLabel={`Manage ${item.title}`}
                      >
                        <MoreVertical size={16} color="#64748B" />
                      </Pressable>
                    )}
                  </View>
                </View>

                {/* Injected action (e.g. "Did you take it?" for CareScreen) — hide for stopped meds */}
                {!stopped && action}
              </View>
            );
          })}
        </View>
      ) : (
        /* Empty state */
        <View
          style={{
            alignItems: "center",
            justifyContent: "center",
            paddingVertical: 24,
            borderRadius: 12,
            borderWidth: 1,
            borderStyle: "dashed",
            borderColor: "rgba(255, 255, 255, 0.1)",
            backgroundColor: "rgba(255, 255, 255, 0.02)",
          }}
        >
          <View
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 12,
            }}
          >
            <Calendar size={20} color="#64748B" />
          </View>
          <Text
            style={{
              fontSize: 14,
              fontWeight: "500",
              color: "#ffffff",
              marginBottom: 4,
            }}
          >
            Nothing scheduled
          </Text>
          <Text
            style={{
              fontSize: 12,
              color: "#64748B",
              textAlign: "center",
              maxWidth: 200,
            }}
          >
            No medications or appointments for this day.
          </Text>
        </View>
      )}

      {/* Cycle section — period / fertile-window / ovulation days */}
      {cycleItems.length > 0 && (
        <View style={{ marginTop: 20 }}>
          <Text
            style={{
              fontSize: 10.4,
              fontWeight: "700",
              color: "#64748B",
              textTransform: "uppercase",
              letterSpacing: 1,
              marginBottom: 12,
            }}
          >
            {t("care.overview.calendar.prediction.cycle")}
          </Text>
          <View style={{ gap: 10 }}>
            {cycleItems.map((item) => (
              <CycleRow key={item.id} item={item} />
            ))}
          </View>

          {/* Estimate + non-contraceptive disclaimer, shown when an estimate is present */}
          {hasFertileEstimate && (
            <View
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTopWidth: 1,
                borderTopColor: "rgba(255, 255, 255, 0.06)",
              }}
            >
              <Text
                style={{
                  fontSize: 11,
                  lineHeight: 16,
                  color: "#64748B",
                  textAlign: "center",
                }}
              >
                {t("care.overview.calendar.prediction.confidence", {
                  params: {
                    level: t(
                      `care.overview.calendar.prediction.confidence_levels.${predictionContext?.predictionConfidence ?? "low"}`,
                    ),
                  },
                })}
              </Text>
              {predictionWarnings.map((warning) => (
                <Text
                  key={warning}
                  style={{
                    fontSize: 11,
                    lineHeight: 16,
                    color: "#64748B",
                    textAlign: "center",
                    marginTop: 4,
                  }}
                >
                  {t(`care.overview.calendar.prediction.warnings.${warning}`)}
                </Text>
              ))}
              <Text
                style={{
                  fontSize: 11,
                  lineHeight: 16,
                  color: "#64748B",
                  textAlign: "center",
                  marginTop: 4,
                }}
              >
                {t("care.overview.calendar.prediction.disclaimer")}
              </Text>
            </View>
          )}
        </View>
      )}
    </View>
  );
}
